import { View } from "react-native";
import React, { useEffect, useState } from "react";
import { useAppSelector } from "hooks/useAppSelector";
import { PlanPackageDetails, SubscriptionDetails } from "features/types";
import UpgradePlanStepOne from "../components/UpgradePlanStepOne";
import HiddenView from "components/HiddenView";
import UpgradePlanStepTwo from "../components/UpgradePlanStepTwo";
import getPlanChanges from "../getPlanChanges";
import { Text } from "@ui-kitten/components";
import { PlanChanges } from "../types";
import { useAccount } from "hooks/useAccount";
import { useMutation } from "@tanstack/react-query";
import { getPlanChangeChargeFetcher } from "queries/account.queries";
import { useAppDispatch } from "hooks/useAppDispatch";
import Button from "components/Button";
import { setStep } from "features/slices/buyFlowSlice";
import { useNavigation } from "@react-navigation/native";
import Spacer from "components/Spacer";
interface UpgradePlansProps {
  selectedSubscription: SubscriptionDetails | null; // the subscription that the user wants to upgrade
  upgradablePlans: PlanPackageDetails[];
}

const UpgradePlans: React.FC<UpgradePlansProps> = ({
  selectedSubscription,
  upgradablePlans,
}) => {
  const dispatch = useAppDispatch();
  const { step } = useAppSelector((state) => state.buyFlow);
  const { account } = useAccount();
  const navigation = useNavigation();
  const [selectedUpgradePlan, setSelectedUpgradePlan] =
    useState<PlanPackageDetails | null>(upgradablePlans[0]); // the plan that the user wants to upgrade to
  const [planChanges, setPlanChanges] = useState<PlanChanges>([]);
  const { data: planChangeCharge, mutateAsync: getPlanChangeCharge } =
    useMutation(getPlanChangeChargeFetcher);

  // watch selectedUpgradePlan changes and fetch signup charge
  useEffect(() => {
    if (!selectedUpgradePlan || !selectedSubscription) return;
    const fetchPlanChangeCharge = async () => {
      const changes = getPlanChanges(
        account!.subscriptions,
        selectedUpgradePlan!,
        selectedSubscription.planPackage!
      );
      await getPlanChangeCharge({
        accountId: account!.id,
        changes,
        source: "FANRALLY_APP",
      });
      setPlanChanges(changes);
    };
    fetchPlanChangeCharge();
  }, [selectedUpgradePlan, selectedSubscription]);

  if (!selectedSubscription) return null;

  if (!planChangeCharge)
    return (
      <Text category="h2" style={{ textAlign: "center" }}>
        loading...
      </Text>
    );

  return (
    <View>
      <HiddenView shouldHide={step !== 1}>
        <UpgradePlanStepOne
          title="Choose a plan to upgrade"
          planPackages={upgradablePlans}
          setSelectedUpgradePlan={setSelectedUpgradePlan}
          selectedUpgradePlan={selectedUpgradePlan}
          planChangeCharge={planChangeCharge}
        />
      </HiddenView>
      <HiddenView shouldHide={step !== 2}>
        {selectedUpgradePlan !== null && (
          <UpgradePlanStepTwo
            planChangeCharge={planChangeCharge}
            selectedUpgradePlan={selectedUpgradePlan}
            selectedSubscription={selectedSubscription}
            planChanges={planChanges}
          />
        )}
      </HiddenView>
      <Spacer />
      <Button
        onPress={() => {
          if (step === 1) {
            navigation.goBack();
          } else {
            dispatch(setStep(step - 1));
          }
        }}
      >
        Back
      </Button>
    </View>
  );
};

export default UpgradePlans;
